import { ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import InfoPopper from "../popper/InfoPopper";
import { FeInfoCircleIcon } from "./FeIcons";

interface Props {
  title: string;
  /** already-formatted value, e.g. "12,450" or "24.5B" */
  value: string;
  /** unit shown after the value, e.g. "tCO₂e" or "THB" */
  unit?: string;
  /** optional info tooltip shown as an ⓘ icon next to the title */
  info?: string;
  /** colored glyph shown in the tinted circle left of the title */
  icon: ReactNode;
  /** glyph color inside the tinted circle */
  iconColor: string;
  /** tinted circle background behind the icon */
  iconBg: string;
  /** grey the value, e.g. when few loans stand behind it */
  muted?: boolean;
  /** control at the right end of the title row, e.g. the intensity basis picker */
  titleAction?: ReactNode;
  /** content at the right end of the value row, e.g. the year-on-year change */
  valueAside?: ReactNode;
}

/**
 * Dashboard stat card — Figma Card/Stat_Card (6229:14897, 6230:9041, 6231:12497).
 * White, radius 8, padding 20, a 12px gap between a 40px title row (32px tinted
 * icon circle · 12 · 14/24 medium title · 8 · 12px info) and a 24px value row
 * (18/24 semibold value · 8 · 12/24 regular #818181 unit) → 116px tall.
 *
 * The two optional slots sit at the right end of those rows so the card keeps
 * Figma's height whatever they hold.
 */
const StatCard = ({
  title,
  value,
  unit,
  info,
  icon,
  iconColor,
  iconBg,
  muted = false,
  titleAction,
  valueAside,
}: Props) => (
  <Box
    height="100%"
    boxSizing="border-box"
    display="flex"
    flexDirection="column"
    justifyContent="center"
    gap="12px"
    p="20px"
    borderRadius="8px"
    bgcolor="var(--white)"
  >
    <Box display="flex" alignItems="center" gap="12px" height={40} minWidth={0}>
      <Box
        width={32}
        height={32}
        borderRadius="24px"
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        color={iconColor}
        bgcolor={iconBg}
        sx={{ "& svg": { fontSize: "17.455px" } }}
      >
        {icon}
      </Box>
      <Box display="flex" alignItems="center" gap="8px" flex={1} minWidth={0}>
        <Typography
          fontSize="14px"
          fontWeight={500}
          lineHeight="24px"
          letterSpacing={0}
          color="var(--dark)"
          noWrap
        >
          {title}
        </Typography>
        {info && (
          // Figma info-circle: vuesax bold glyph, 12px, #818181
          <InfoPopper text={info} icon={FeInfoCircleIcon} iconSize={12} iconColor="var(--gray1)" />
        )}
      </Box>
      {titleAction}
    </Box>

    <Box display="flex" alignItems="center" gap="8px" height={24} minWidth={0}>
      <Typography
        fontSize="18px"
        fontWeight={600}
        lineHeight="24px"
        letterSpacing={0}
        color={muted ? "var(--gray1)" : "var(--dark)"}
        noWrap
        flexShrink={0}
      >
        {value}
      </Typography>
      {unit && (
        <Typography
          fontSize="12px"
          fontWeight={400}
          lineHeight="24px"
          letterSpacing={0}
          color="var(--gray1)"
          noWrap
          flexShrink={0}
        >
          {unit}
        </Typography>
      )}
      {valueAside && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap="8px"
          flex={1}
          minWidth={0}
        >
          {valueAside}
        </Box>
      )}
    </Box>
  </Box>
);

export default StatCard;
